import { observable, action, makeObservable } from 'mobx';
import { makePersistable } from 'mobx-persist-store';
import { ICohortDetails } from '../_components/organisms/CreateNewModalCoreComponent';
import { buildEmptyCohort } from '../core-utils/helper-utils/cohortModalHelper';
import { ToggleValue } from '../core-utils/custom-hooks/useReportsCohortItem';
import LZString from 'lz-string';
import { FilterFlowDataProps } from '../_components/atoms/FilterFlow';

export class CohortDetailsStore {
  categoryDropDownOptions: string[] = [];
  conditionDropDownOptions: string[][] = [];
  cohortSelectionsTypes: string[] = [];
  propertyDropDownOptions: string[][] = [];
  propertyDropDownOptionsOriginal: string[][] = [];

  cohortDetailsList: ICohortDetails[] = [buildEmptyCohort(1)];
  claimIdList: string[] = [];
  memberUniqueIdList: string[] = [];
  conditionsDetails: string[] = [];

  draftCohortDetailsList: ICohortDetails[] = [buildEmptyCohort(1)];
  draftMemberUniqueIdList: string[] = [];

  toggleList: ToggleValue[] = [];
  draftFilterFlowData: FilterFlowDataProps[] = [
    {
      label: 'Total No. of Patients',
      count: 0,
      percent: '100%',
      type: 'TOTAL',
    },
  ];
  isLoadingCohort: boolean = false;

  cohortValues: any = {
    chronic_conditions: [],
    high_cost_claimants: '',
    multiple_drugs: '',
  };
  openCohortModal: boolean = false;

  constructor() {
    makeObservable(this, {
      categoryDropDownOptions: observable,
      conditionDropDownOptions: observable,
      cohortSelectionsTypes: observable,
      propertyDropDownOptions: observable,
      cohortDetailsList: observable,
      claimIdList: observable,
      memberUniqueIdList: observable,
      conditionsDetails: observable,
      propertyDropDownOptionsOriginal: observable,

      draftCohortDetailsList: observable,
      draftMemberUniqueIdList: observable,
      toggleList: observable,
      draftFilterFlowData: observable,
      isLoadingCohort: observable,

      setCohortDetailsList: action,
      addClaimIdList: action,
      addMemberUniqueIdList: action,
      removeClaimIdList: action,
      removeCohortDetailsList: action,
      removeMemberUniqueIdList: action,
      setCohortDetailsDrillDownData: action,
      removeCohortDetailsDrillDownData: action,
      setConditionsDetails: action,
      removeConditionsDetails: action,
      cohortValues: observable,
      openCohortModal: observable,
      setCohortValues: action,
      toggleCohortModal: action,
      setPropertyDropDownOptions: action,
      setPropertyDropDownOptionsOriginal: action,

      initDraft: action,
      commitDraft: action,
      discardDraft: action,
      setDraftCohortDetailsList: action,
      addDraftMemberUniqueIdList: action,
      setToggleList: action,
      setDraftFilterFlowData: action,
      setIsLoadingCohort: action,
      resetMemberData: action,
    });

    makePersistable(this, {
      name: 'CohortDetailsStore',
      properties: [
        'categoryDropDownOptions',
        'conditionDropDownOptions',
        'cohortSelectionsTypes',
        'cohortDetailsList',
        'claimIdList',
        'memberUniqueIdList',
        'conditionsDetails',
        'propertyDropDownOptionsOriginal',
        'toggleList',
      ],
      storage: window.localStorage,
    });

    makePersistable(this, {
      name: 'CohortDetailsStoreProperties',
      properties: [
        {
          key: 'propertyDropDownOptions',
          serialize: (value) => LZString.compress(JSON.stringify(value)),
          deserialize: (value) => JSON.parse(LZString.decompress(value)),
        },
      ],
      storage: window.localStorage,
    });
  }

  setCohortDetailsDrillDownData(
    categoryDropDownOptions: string[] = [],
    conditionDropDownOptions: string[][] = [],
    cohortSelectionsTypes: string[] = [],
    propertyDropDownOptions: string[][],
  ) {
    this.categoryDropDownOptions = categoryDropDownOptions;
    this.conditionDropDownOptions = conditionDropDownOptions;
    this.cohortSelectionsTypes = cohortSelectionsTypes;
    this.propertyDropDownOptions = propertyDropDownOptions;
    this.propertyDropDownOptionsOriginal = propertyDropDownOptions;
  }

  setPropertyDropDownOptionsOriginal(options: string[][]) {
    this.propertyDropDownOptionsOriginal = options;
  }

  setPropertyDropDownOptions(options: string[][]) {
    this.propertyDropDownOptions = options;
  }

  removeCohortDetailsDrillDownData() {
    this.categoryDropDownOptions = [];
    this.conditionDropDownOptions = [];
    this.cohortSelectionsTypes = [];
  }

  addClaimIdList(claimIdList: string[]) {
    this.claimIdList = claimIdList;
  }

  removeClaimIdList() {
    this.claimIdList = [];
  }

  setCohortDetailsList(cohortDetailsList: ICohortDetails[]) {
    this.cohortDetailsList = cohortDetailsList;
  }

  removeCohortDetailsList() {
    this.cohortDetailsList = [
      {
        id: 1,
        category: '',
        condition: '',
        property: '',
        results: '',
        dropdownSelectionTypeIndex: 0,
        andOrCondition: 'AND',
        propertiesPageNumber: 0,
        totalPagesForProperties: 0,
      },
    ];
  }

  setConditionsDetails(conditionsDetails: string[]) {
    this.conditionsDetails = conditionsDetails;
  }

  removeConditionsDetails() {
    this.conditionsDetails = [];
  }

  addMemberUniqueIdList(memberUniqueIdList: string[]) {
    this.memberUniqueIdList = memberUniqueIdList;
  }

  removeMemberUniqueIdList() {
    this.memberUniqueIdList = [];
  }

  setCohortValues(values: any) {
    this.cohortValues = values;
  }

  toggleCohortModal(value: boolean) {
    this.openCohortModal = value;
  }
  setDraftFilterFlowData(data: FilterFlowDataProps[]) {
    this.draftFilterFlowData = data;
  }
  setIsLoadingCohort(value: boolean) {
    this.isLoadingCohort = value;
  }

  initDraft(count: number) {
    this.draftCohortDetailsList = [...this.cohortDetailsList];
    this.draftMemberUniqueIdList = [...this.memberUniqueIdList];
    this.draftFilterFlowData = [
      {
        label: 'Total No. of Patients',
        count,
        percent: '100%',
        type: 'TOTAL',
      },
    ];
    this.isLoadingCohort = false;
    this.propertyDropDownOptions = [...this.propertyDropDownOptionsOriginal];
    this.toggleList = this.cohortDetailsList.slice(0, -1).map((cohort) => ({
      toggleValue: cohort.andOrCondition?.toLowerCase() as 'and' | 'or',
    }));
  }

  resetDraft(count: number) {
    this.draftCohortDetailsList = [buildEmptyCohort(1)];
    this.draftMemberUniqueIdList = [];
    this.draftFilterFlowData = [
      {
        label: 'Total No. of Patients',
        count,
        percent: '100%',
        type: 'TOTAL',
      },
    ];
    this.isLoadingCohort = false;
    this.propertyDropDownOptions = [...this.propertyDropDownOptionsOriginal];
    this.toggleList = [];
  }

  commitDraft() {
    this.cohortDetailsList = [...this.draftCohortDetailsList];
    this.memberUniqueIdList = [...this.draftMemberUniqueIdList];
    this.discardDraft();
  }

  discardDraft() {
    this.draftCohortDetailsList = [];
    this.draftMemberUniqueIdList = [];
  }

  setDraftCohortDetailsList(list: ICohortDetails[]) {
    this.draftCohortDetailsList = list;
  }

  addDraftMemberUniqueIdList(list: string[]) {
    this.draftMemberUniqueIdList = list;
  }

  setToggleList(list: ToggleValue[]) {
    this.toggleList = list;
  }

  resetMemberData() {
    this.memberUniqueIdList = [];
    this.claimIdList = [];
  }
}
export default CohortDetailsStore;
